import { type DateFormats, formatDate, formatTime } from './dateFormat';
import { civilParts, dayIndex } from './timeZone';
import { formatTenantMoney } from './tenantCurrency';

/** Pure display formatters for the Communications module — tested. */

/** "Eleanor Whitfield" → "EW", "@wanderlust.kate" → "WK", "+1 212…" → "#". */
export function initialsOf(name: string): string {
  const words = name
    .replace(/^@/, '')
    .split(/[\s.·_-]+/)
    .filter((w) => /[a-zA-Z]/.test(w));
  if (words.length === 0) return '#';
  const first = words[0][0] ?? '';
  const second = words.length > 1 ? (words[1][0] ?? '') : '';
  return (first + second).toUpperCase() || '#';
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * The weekday name for the CIVIL date in `zone`. Derived from the civil parts
 * rather than asked of Intl a second time, so a row's weekday can never come
 * from a different clock than the date printed beside it.
 */
function weekdayIn(date: Date, zone: string | null | undefined): string {
  const { year, month, day } = civilParts(date, zone);
  return WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
}

/**
 * Conversation-list timestamp, matching the design rows: today → the clock,
 * yesterday → "Yesterday", within a week → "Mon", older → the date.
 *
 * `zone` is the tenant's dispatch timezone (null → the viewer's own) and
 * `formats` the tenant's patterns; the clock and the older-than-a-week date are
 * both written the way dispatch writes them. Both the clock and the day
 * boundaries move with the zone — see dayIndex.
 *
 * A row older than a week deliberately shows the DATE alone: the full pattern
 * with a year in it ("02/09/2026") is as much as this column can hold, and the
 * time is one click away in the thread.
 */
export function listTime(
  iso: string | null,
  now: Date = new Date(),
  zone?: string | null,
  formats?: DateFormats,
): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const dayDiff = dayIndex(now, zone) - dayIndex(date, zone);

  if (dayDiff <= 0) return formatTime(date, zone, formats?.timeFormat);
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return weekdayIn(date, zone);
  return formatDate(date, zone, formats?.dateFormat);
}

/** Message timestamp: "Today 09:41 am" / "Mon 16:20" / "12/03/2026 10:30". */
export function messageTime(
  iso: string | null,
  now: Date = new Date(),
  zone?: string | null,
  formats?: DateFormats,
): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const time = formatTime(date, zone, formats?.timeFormat);
  const dayDiff = dayIndex(now, zone) - dayIndex(date, zone);
  if (dayDiff <= 0) return `Today ${time}`;
  if (dayDiff === 1) return `Yesterday ${time}`;
  if (dayDiff < 7) return `${weekdayIn(date, zone)} ${time}`;
  return `${formatDate(date, zone, formats?.dateFormat)} ${time}`;
}

/** Compact elapsed time for the waiting chip: "12m", "3h", "2d". */
export function waitingFor(sinceIso: string | null, now: Date = new Date()): string | null {
  if (!sinceIso) return null;
  const since = new Date(sinceIso).getTime();
  if (Number.isNaN(since)) return null;
  const minutes = Math.floor((now.getTime() - since) / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** SMS length label: "112 characters · 1 segment". Over 160 → multi-segment. */
export function smsCount(length: number): { label: string; over: boolean } {
  const segments = Math.max(1, Math.ceil(length / 160));
  return {
    label: `${length} characters · ${segments} segment${segments === 1 ? '' : 's'}`,
    over: segments > 1,
  };
}

/** "04:12" from seconds; "—" when unknown. */
export function callDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** "CA$8,940" — dispatch money snapshots are plain numbers, in the tenant's currency. */
export function money(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return formatTenantMoney(value, 0);
}

/** Percentage with one decimal: 0.974 → "97.4%". */
export function pct(fraction: number): string {
  if (!Number.isFinite(fraction)) return '—';
  return `${(fraction * 100).toFixed(1)}%`;
}

/** First line of a message body, plain-text-ified, for list snippets. */
export function snippetOf(content: string | null): string {
  if (!content) return '';
  return content
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);
}

/** Plain text moved into the styled editor: escape it, keep the line breaks. */
export function escapeToHtml(text: string): string {
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped.split('\n').join('<br>');
}
